import { NgZone, inject } from '@angular/core';

/**
 * User-intent gate for infinite-scroll pagination.
 *
 * Problem it solves: a fast wheel flick queues smooth scrolling that keeps
 * running after a page response lands, carrying the viewport through the
 * freshly appended rows to the new bottom. The scroll trigger (ngx
 * infiniteScroll with alwaysCallback, or an IntersectionObserver sentinel)
 * then fires again and the NEXT page loads with no further input from the
 * user — pages "chain" on one flick. Scroll events cannot distinguish that
 * tail from a real scroll; input events can: the tail produces no new wheel
 * notches, touch moves or scroll keys.
 *
 * Rule: after a page lands the gate is DISARMED. A wheel-down / touch-move /
 * scroll-key event re-arms it, but only if it is a NEW gesture — i.e. it
 * follows a quiet gap of `restMs` in the whole input stream (wheel, touch,
 * keys AND scroll events). A single spin's leftover notches (which keep
 * arriving for a few hundred ms after the page landed) never have that gap.
 * The quiet-gap rule only applies for `settleMs` after the page landed; past
 * that, any qualifying input arms on its own, so a steady slow scroll (notches
 * 100–200 ms apart, never a 300 ms pause) can still load the next page when
 * it reaches the bottom.
 *
 * If the arming input arrives while the list is already at its bottom
 * (short list, nothing left to scroll — no scroll event will ever come),
 * `onArmAtBottom` is invoked inside the Angular zone so the component can
 * fetch immediately.
 *
 * Usage (component):
 *   readonly scrollGate = new ScrollIntentGate({
 *     onArmAtBottom: () => this.onScrollDown(),
 *     isAtBottom: () => ScrollIntentGate.nearBottom(document.querySelector('.jc-v3-entity-details-body')),
 *   });
 *   ngOnInit()    { this.scrollGate.attach(); }
 *   ngOnDestroy() { this.scrollGate.detach(); }
 *   onScrollDown() { if (!this.loadAPIcall && !this.stopscrollFlag && this.scrollGate.armed) { ... } }
 *   // wherever a page finishes loading (after `this.loadAPIcall = false`):
 *   this.scrollGate.pageLanded();
 *
 * Must be constructed in an injection context (a component field initializer
 * or constructor) because it resolves NgZone via inject().
 */
export interface ScrollIntentGateOptions {
  /** Invoked (in the Angular zone) when an input arms the gate while the list is already at the bottom. */
  onArmAtBottom: () => void;
  /** Whether the list is currently scrolled to / near its bottom. */
  isAtBottom: () => boolean;
  /** Quiet gap that separates two gestures. Default 300 ms. */
  restMs?: number;
  /** Window after a page lands during which the quiet-gap rule applies. Default 1000 ms. */
  settleMs?: number;
}

export class ScrollIntentGate {
  private readonly zone = inject(NgZone);
  private readonly restMs: number;
  private readonly settleMs: number;
  private readonly scrollKeys = new Set(['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar']);

  /** True when the next page may be fetched. Starts armed so the first scroll works untouched. */
  armed = true;
  private lastInputAt = 0;
  private lastPageLandedAt = 0;
  private attached = false;

  private readonly onWheel = (e: WheelEvent) => this.noteInput(e.deltaY > 0);
  private readonly onTouch = () => this.noteInput(true);
  private readonly onKey = (e: KeyboardEvent) => this.noteInput(this.scrollKeys.has(e.key));
  // Scroll events only keep the stream busy (smooth-scroll tail, touch
  // inertia); they never arm on their own, so a scroll-anchoring nudge right
  // after a page lands can't be mistaken for a gesture.
  private readonly onScroll = () => this.noteInput(false);

  constructor(private readonly opts: ScrollIntentGateOptions) {
    this.restMs = opts.restMs ?? 300;
    this.settleMs = opts.settleMs ?? 1000;
  }

  /** Container scrolled to within `heights` × its own height of the end (or not scrollable at all). */
  static nearBottom(el: Element | null, heights = 2): boolean {
    if (!el) return false;
    const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return remaining <= Math.max(1, el.clientHeight) * heights;
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('wheel', this.onWheel, { passive: true });
      document.addEventListener('touchmove', this.onTouch, { passive: true });
      document.addEventListener('keydown', this.onKey, { passive: true });
      // scroll doesn't bubble; capture catches it from any scroll container.
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    });
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    document.removeEventListener('wheel', this.onWheel);
    document.removeEventListener('touchmove', this.onTouch);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('scroll', this.onScroll, { capture: true });
  }

  /**
   * Call after every page load completes (success or error). Disarms until
   * the user makes a new gesture. Disarming here — rather than when the
   * fetch starts — means notches delivered WHILE the page was loading don't
   * count.
   */
  pageLanded(): void {
    this.armed = false;
    this.lastPageLandedAt = performance.now();
  }

  private noteInput(canArm: boolean): void {
    const now = performance.now();
    const gap = now - this.lastInputAt;
    this.lastInputAt = now;
    if (!canArm || this.armed) return;
    const withinSettle = (now - this.lastPageLandedAt) < this.settleMs;
    if (withinSettle && gap < this.restMs) return;
    this.armed = true;
    if (this.opts.isAtBottom()) {
      this.zone.run(() => this.opts.onArmAtBottom());
    }
  }
}
